import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { LuArrowLeft, LuArrowRight, LuCheck } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

const API = process.env.REACT_APP_API_URL || '';
const DESKTOP_QUERY = '(min-width: 768px)';

function useDesktopLayout() {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(DESKTOP_QUERY).matches : false
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(DESKTOP_QUERY);
    const update = () => setDesktop(media.matches);
    update();
    if (media.addEventListener) media.addEventListener('change', update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', update);
      else media.removeListener(update);
    };
  }, []);
  return desktop;
}

/**
 * Pass `agreement` from GET /referral-partners/me.
 * onAccepted must reload /me before showing the dashboard/code.
 * onReloadAgreement reloads /me after a stale-version response (409).
 * onBack should navigate away from the creator dashboard, not dismiss its gate.
 */
export default function CreatorAgreementGate({
  open = true,
  agreement,
  accessToken,
  onAccepted,
  onReloadAgreement,
  onBack,
}) {
  if (!open) return null;
  // A different document must start with fresh consent and a new signature.
  const documentKey = `${agreement?.version || ''}:${agreement?.contentHash || ''}`;
  return <AgreementForm key={documentKey} agreement={agreement} accessToken={accessToken}
    onAccepted={onAccepted} onReloadAgreement={onReloadAgreement} onBack={onBack} />;
}

function AgreementForm({ agreement, accessToken, onAccepted, onReloadAgreement, onBack }) {
  const desktop = useDesktopLayout();
  const id = useId();
  const sheetRef = useRef(null);
  const headingRef = useRef(null);
  const bodyRef = useRef(null);
  const documentRef = useRef(null);
  const endRef = useRef(null);
  const requestRef = useRef(null);
  const busyRef = useRef(false);
  const aliveRef = useRef(false);
  const [readToEnd, setReadToEnd] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureConsent, setSignatureConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(null);

  const validAgreement = ['version', 'contentHash', 'title', 'text'].every(key =>
    typeof agreement?.[key] === 'string' && agreement[key].trim().length > 0
  );
  const validName = signatureName.trim().length > 0 && signatureName.trim().length <= 150
    && /\p{L}/u.test(signatureName) && !/[\u0000-\u001F\u007F]/u.test(signatureName);
  const canSign = validAgreement && readToEnd && acknowledged && validName && signatureConsent
    && !saving && !reloading && !stale && !saved;

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; requestRef.current?.abort(); };
  }, []);

  useEffect(() => { if (saved) headingRef.current?.focus(); }, [saved]);

  useEffect(() => {
    const previous = document.activeElement;
    headingRef.current?.focus({ preventScroll: desktop });
    if (!desktop) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const trapFocus = event => {
      if (event.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const items = Array.from(sheet.querySelectorAll(
        'a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]'
      ));
      if (!items.length) { event.preventDefault(); headingRef.current?.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !items.includes(active))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (active === last || !sheet.contains(active) || active === headingRef.current)) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', trapFocus);
      if (previous?.isConnected && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    };
  }, [desktop]);

  useEffect(() => {
    if (!validAgreement || saved) return undefined;
    const reader = documentRef.current;
    const end = endRef.current;
    if (!reader || !end) return undefined;
    const root = desktop ? bodyRef.current : null;
    const checkPosition = () => {
      const docRect = reader.getBoundingClientRect();
      const viewport = root?.getBoundingClientRect();
      const lowerEdge = viewport ? viewport.bottom : window.innerHeight;
      if (docRect.height > 0 && docRect.bottom <= lowerEdge + 2) setReadToEnd(true);
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) setReadToEnd(true);
    }, { root, threshold: 1 }) : null;
    observer?.observe(end);
    checkPosition(); // Short documents must not require a scroll that isn't possible.
    root?.addEventListener('scroll', checkPosition, { passive: true });
    window.addEventListener('scroll', checkPosition, true);
    window.addEventListener('resize', checkPosition);
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(checkPosition) : null;
    resize?.observe(reader);
    return () => {
      observer?.disconnect(); resize?.disconnect();
      root?.removeEventListener('scroll', checkPosition);
      window.removeEventListener('scroll', checkPosition, true);
      window.removeEventListener('resize', checkPosition);
    };
  }, [desktop, validAgreement, saved, reloadCount]);

  async function continueToDashboard(result) {
    if (typeof onAccepted === 'function') await onAccepted(result);
    else window.location.reload();
  }

  async function reloadAgreement() {
    if (reloading || saving) return;
    setReloading(true); setError('');
    try {
      if (typeof onReloadAgreement === 'function') {
        await onReloadAgreement();
        if (aliveRef.current) {
          setStale(false); setReadToEnd(false); setAcknowledged(false); setSignatureConsent(false);
          setReloadCount(count => count + 1);
        }
      } else window.location.reload();
    } catch {
      if (aliveRef.current) setError('We couldn’t reload the agreement. Please try again.');
    } finally { if (aliveRef.current) setReloading(false); }
  }

  async function submit(event) {
    event.preventDefault();
    if (!canSign || busyRef.current) return;
    busyRef.current = true; setSaving(true); setError('');
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const token = accessToken || window.localStorage.getItem('accessToken');
      if (!token) { setError('Please sign in again to accept your creator agreement.'); return; }
      const response = await axios.post(`${API}/referral-partners/me/agreement/accept`, {
        version: agreement.version,
        contentHash: agreement.contentHash,
        signatureName: signatureName.trim().replace(/\s+/g, ' '),
        acknowledged: true,
        electronicSignatureConsent: true,
      }, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
      if (!aliveRef.current || controller.signal.aborted) return;
      const result = response.data;
      if (result?.requiresAgreement !== false || result?.agreementStatus?.accepted !== true) {
        setError('We couldn’t confirm acceptance. Reload the agreement and try again.');
        return;
      }
      setSaved(result);
      try { await continueToDashboard(result); }
      catch { if (aliveRef.current) setError('Your agreement was saved. Please try opening your dashboard again.'); }
    } catch (err) {
      if (!aliveRef.current || controller.signal.aborted || axios.isCancel?.(err)) return;
      if (err?.response?.status === 409) {
        setStale(true);
        setError('The agreement has changed. Reload it and review the new version before signing.');
      } else if (err?.response?.status === 401) {
        setError('Your session has expired. Please sign in again to continue.');
      } else setError(err?.response?.data?.message || 'We couldn’t save your agreement. Please try again.');
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setSaving(false);
    }
  }

  const content = <div className={`cag-root ${desktop ? 'cag-desktop' : 'cag-mobile'}`}>
    <style>{STYLES}</style>
    <section ref={sheetRef} className="cag-sheet" role={desktop ? 'dialog' : undefined}
      aria-modal={desktop ? true : undefined} aria-labelledby={`${id}-title`} aria-describedby={`${id}-intro`}>
      <header className="cag-header">
        {onBack ? <button type="button" className="cag-back" onClick={onBack} disabled={saving}
          aria-label="Back to Tribe"><LuArrowLeft size={19} aria-hidden="true" /></button>
          : <a className="cag-back" href="/events" aria-label="Back to Tribe"><LuArrowLeft size={19} aria-hidden="true" /></a>}
        <div className="cag-logo"><TribeLogo aria-label="Tribe" /></div>
      </header>
      {saved ? <div className="cag-success" role="status">
        <span className="cag-success-icon"><LuCheck size={22} aria-hidden="true" /></span>
        <h1 id={`${id}-title`} ref={headingRef} tabIndex={-1}>Agreement accepted</h1>
        <p id={`${id}-intro`}>Your signature has been saved. You’re ready to use your creator dashboard.</p>
        {error ? <p className="cag-error" role="alert">{error}</p> : null}
        <button type="button" className="cag-primary" disabled={saving} onClick={async () => {
          setError('');
          try { await continueToDashboard(saved); }
          catch { if (aliveRef.current) setError('We couldn’t open your dashboard. Please try again.'); }
        }}>Continue to dashboard <LuArrowRight size={16} aria-hidden="true" /></button>
      </div> : <form className="cag-form" onSubmit={submit} noValidate>
        <div ref={bodyRef} className="cag-body" tabIndex={desktop ? 0 : undefined} aria-label={desktop ? 'Agreement review and signature' : undefined}>
          <div className="cag-intro">
            <h1 id={`${id}-title`} ref={headingRef} tabIndex={-1}>Your creator agreement</h1>
            <p id={`${id}-intro`}>Review and sign to access your dashboard and personal invite code.</p>
          </div>
          {!validAgreement ? <div className="cag-unavailable" role="alert">
            <p>We couldn’t load your agreement.</p>
            <button type="button" className="cag-text-button" onClick={reloadAgreement} disabled={reloading}>
              {reloading ? 'Reloading…' : 'Try again'}
            </button>
          </div> : <>
            <div className="cag-document-heading"><span>{agreement.title}</span><span>Version {agreement.version}</span></div>
            <article ref={documentRef} className="cag-document" aria-label={agreement.title}>
              {agreement.text}
              <span ref={endRef} className="cag-document-end" aria-hidden="true" />
            </article>
            <label className="cag-choice">
              <input type="checkbox" checked={acknowledged} disabled={!readToEnd || saving || stale || reloading}
                onChange={event => setAcknowledged(event.target.checked)} />
              <span>I have read and agree to the creator agreement.</span>
            </label>
            {!readToEnd ? <p className="cag-hint">Scroll to the end of the agreement to enable acceptance.</p> : null}
            <div className="cag-signature">
              <label className="cag-label" htmlFor={`${id}-name`}>Full legal name</label>
              <input id={`${id}-name`} type="text" autoComplete="name" maxLength={150} value={signatureName}
                onChange={event => setSignatureName(event.target.value)} disabled={saving || stale || reloading}
                placeholder="Enter your full name" aria-describedby={`${id}-name-hint`} />
              <p id={`${id}-name-hint`} className="cag-hint">Your typed name will be recorded as your signature.</p>
            </div>
            <label className="cag-choice">
              <input type="checkbox" checked={signatureConsent} disabled={saving || stale || reloading}
                onChange={event => setSignatureConsent(event.target.checked)} />
              <span>I consent to using my typed name as my electronic signature to accept this agreement.</span>
            </label>
          </>}
          {error ? <div className="cag-error" role="alert">{error}</div> : null}
        </div>
        <footer className="cag-footer">
          <a href="mailto:nati@jointribe.io">Questions? Email Tribe</a>
          {stale ? <button type="button" className="cag-primary" onClick={reloadAgreement} disabled={reloading}>
            {reloading ? 'Reloading…' : 'Reload agreement'}
          </button> : <button type="submit" className="cag-primary" disabled={!canSign} aria-busy={saving}>
            {saving ? 'Saving your signature…' : 'Sign & continue'} {!saving ? <LuArrowRight size={16} aria-hidden="true" /> : null}
          </button>}
        </footer>
      </form>}
    </section>
  </div>;
  return desktop && typeof document !== 'undefined' && document.body ? createPortal(content, document.body) : content;
}

const STYLES = `
.cag-root,.cag-root *{box-sizing:border-box}.cag-root{font-family:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#222;background:#fff;min-height:100vh;min-height:100dvh}.cag-root button,.cag-root input{font:inherit}.cag-root button{cursor:pointer}.cag-root button:disabled{cursor:not-allowed}.cag-root a{color:inherit}.cag-root :focus-visible{outline:2px solid #555;outline-offset:4px}.cag-root h1:focus{outline:none}
.cag-sheet{width:100%;min-height:100vh;min-height:100dvh;background:#fff;display:flex;flex-direction:column}.cag-header{height:65px;flex-shrink:0;position:relative;display:flex;align-items:center;justify-content:center;border-bottom:1px solid #ecece8;padding:0 24px}.cag-back{position:absolute;left:20px;top:50%;transform:translateY(-50%);width:36px;height:36px;display:grid;place-items:center;background:none;border:0;border-radius:6px;text-decoration:none;color:#444}.cag-back:hover{background:#f5f5f1}.cag-logo{display:flex;align-items:center;justify-content:center}.cag-logo svg{display:block;width:66px;height:auto;max-height:26px}
.cag-form{display:flex;flex-direction:column;flex:1;min-height:0}.cag-body{padding:28px 22px 32px}.cag-intro{margin-bottom:26px}.cag-intro h1,.cag-success h1{font-size:25px;line-height:1.2;letter-spacing:-.035em;font-weight:600;margin:0 0 10px}.cag-intro p,.cag-success p{color:#686862;font-size:14px;line-height:1.65;margin:0}.cag-document-heading{display:flex;flex-direction:column;gap:5px;font-size:12px;color:#77776f;margin-bottom:12px}.cag-document-heading span:first-child{color:#333;font-weight:550}.cag-document{white-space:pre-wrap;overflow-wrap:anywhere;font-weight:400;font-family:inherit;font-size:14px;line-height:1.8;background:#f8f8f5;border:1px solid #e8e8e2;border-radius:10px;padding:20px 18px;margin-bottom:22px;color:#41413c}.cag-document-end{display:block;height:1px;width:1px}
.cag-choice{display:flex;align-items:flex-start;gap:10px;font-size:13px;line-height:1.6;cursor:pointer}.cag-choice input{width:17px;height:17px;min-width:17px;margin:2px 0 0;accent-color:#252525;cursor:pointer}.cag-choice input:disabled{cursor:not-allowed}.cag-hint{font-size:12px;line-height:1.55;color:#77776f;margin:7px 0 0}.cag-signature{margin:26px 0 18px}.cag-label{display:block;font-size:13px;font-weight:550;margin-bottom:8px}.cag-signature>input{display:block;width:100%;min-height:46px;border:1px solid #dcdcd5;border-radius:7px;padding:11px 12px;font-size:16px;color:#222;background:#fff}.cag-signature>input:disabled{background:#f6f6f3;color:#777}.cag-signature>input::placeholder{color:#999990}
.cag-footer{position:sticky;bottom:0;display:flex;flex-direction:column-reverse;gap:15px;align-items:stretch;border-top:1px solid #ecece8;padding:16px 22px calc(16px + env(safe-area-inset-bottom,0px));background:#fff;margin-top:auto}.cag-footer>a{font-size:12px;color:#77776f;text-decoration:none;text-align:center}.cag-footer>a:hover{text-decoration:underline}.cag-primary{display:inline-flex;align-items:center;justify-content:center;gap:12px;min-height:46px;padding:12px 20px;background:#242424;border:1px solid #242424;border-radius:7px;color:#fff;font-size:13px;font-weight:550}.cag-primary:hover:not(:disabled){background:#080808}.cag-primary:disabled{background:#eeeee9;border-color:#eeeee9;color:#96968e}.cag-error{padding:12px 14px;border-radius:7px;border:1px solid #f0d1ce;background:#fff5f3;color:#9b3029;font-size:13px;line-height:1.6;margin-top:18px}.cag-unavailable{padding:26px 0;color:#686862;font-size:14px}.cag-text-button{border:0;background:none;padding:8px 0;font-size:13px;color:#333;text-decoration:underline}.cag-success{padding:44px 26px;text-align:center}.cag-success-icon{display:grid;place-items:center;width:44px;height:44px;margin:0 auto 20px;border:1px solid #dce4da;border-radius:50%;background:#f1f6ef;color:#45643c}.cag-success .cag-primary{margin-top:24px}
@media(min-width:768px){.cag-root.cag-desktop{position:fixed;inset:0;z-index:100000;background:rgba(20,20,18,.44);display:flex;align-items:center;justify-content:center;padding:24px;min-height:0}.cag-desktop .cag-sheet{width:min(720px,100%);min-height:0;max-height:90vh;max-height:90dvh;border:1px solid #e7e7e0;border-radius:14px;box-shadow:0 18px 70px rgba(0,0,0,.16);overflow:hidden}.cag-desktop .cag-body{overflow-y:auto;overscroll-behavior:contain;min-height:0;padding:30px 32px 28px}.cag-desktop .cag-intro h1{font-size:28px}.cag-desktop .cag-document-heading{flex-direction:row;justify-content:space-between;gap:20px}.cag-desktop .cag-document{padding:24px}.cag-desktop .cag-footer{position:static;flex-shrink:0;flex-direction:row;align-items:center;justify-content:space-between;padding:16px 32px}.cag-desktop .cag-footer>a{text-align:left}.cag-desktop .cag-primary{min-width:170px}.cag-desktop .cag-success{overflow-y:auto;min-height:0}}
@media(prefers-reduced-motion:reduce){.cag-root *{scroll-behavior:auto!important}}
`;
